<template>
  <div v-if="client">
    <base-page-heading
      :title="client.name"
      show-back-button
    >
      <template #secondary-title>
        <sfs-active-badge :active="!client.deleted_at" />
      </template>
      <template #extra>
        <b-dropdown
          v-if="$can('update', 'Client')"
          variant="primary"
          split
          @click="$bvModal.show('client-edit-modal')"
          class="d-none d-md-inline"
          no-flip
          right
          v-b-tooltip.hover
          title="Edit the Client"
        >
          <template #button-content>
            <i class="fa fa-pencil-alt mr-1" /> Edit
          </template>
          <b-dropdown-item
            v-b-modal.client-settings-modal
          >
            <i class="fa fa-pencil-alt mr-1" />
            Edit Settings
          </b-dropdown-item>
          <b-dropdown-item
            v-b-modal.client-billing-details-modal
          >
            <i class="fa fa-pencil-alt mr-1" />
            Edit Billing Details
          </b-dropdown-item>
        </b-dropdown>

        <net-suite-form-popover
          v-if="$can('read', 'IntegrationReference')"
          :reference-id="client.id"
          reference-type="Client"
          :current-integration-reference-id="client.netsuite_reference_id"
          button-class="m-0"
        />

        <b-dropdown
          variant="outline-secondary"
          no-caret
          right
        >
          <template #button-content>
            <i class="fa fa-ellipsis-v" />
          </template>

          <div class="d-md-none">
            <b-dropdown-item
              v-if="$can('update', 'Client')"
              v-b-modal.client-edit-modal
            >
              <i class="fa fa-pencil-alt mr-1" /> Edit
            </b-dropdown-item>

            <b-dropdown-item
              v-if="$can('update', 'Client')"
              v-b-modal.client-settings-modal
            >
              <i class="fa fa-pencil-alt mr-1" /> Edit Settings
            </b-dropdown-item>

            <b-dropdown-item
              v-b-modal.client-billing-details-modal
            >
              <i class="fa fa-pencil-alt mr-1" />
              Edit Billing Details
            </b-dropdown-item>

            <b-dropdown-divider />
          </div>

          <b-dropdown-item
            v-if="$can('destroy', 'Client')"
            @click="toggleActive"
          >
            <i
              v-if="!client.deleted_at"
              class="fa fa-trash mr-1"
            />
            <i
              v-else
              class="fa fa-undo mr-1"
            />
            {{ toggleActiveTooltipText }}
          </b-dropdown-item>

          <b-dropdown-item
            v-if="$can('read', 'Version')"
            :to="{ name: 'versions', query: { model_id: client.id, model: 'Client' } }"
          >
            <i class="fa fa-glasses mr-1" />
            Audit Log
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </base-page-heading>
    <div class="content pt-lg-5">
      <b-row>
        <b-col md="6">
          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Code
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ client.code }}
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Customer ID
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="client.customer_identifier">
                  {{ client.customer_identifier }}
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Address
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="client.address">
                  {{ client.address }}
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  City
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="client.city">
                  {{ client.city }}
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  State
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="client.state">
                  {{ client.state }}
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Zip Code
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="client.zip_code">
                  {{ client.zip_code }}
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12">
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Managers
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="client.managers && client.managers.length > 0">
                  <b-link
                    v-for="(manager, index) in client.managers"
                    :key="index"
                    :to="{ name: 'user', params: { id: manager.id } }"
                  >
                    {{ manager.name }}<span v-if="index != client.managers.length - 1">,</span>
                  </b-link>
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <hr class="mt-0 mb-3">

          <b-row>
            <b-col>
              <b-row>
                <b-col
                  cols="12"
                  sm="6"
                >
                  <b-row class="text-muted font-w500 font-size-sm mb-1">
                    <b-col>
                      Generate invoices for work orders individually?
                    </b-col>
                  </b-row>

                  <b-row class="mb-3">
                    <b-col>
                      <span v-if="client.invoice_work_orders_individually">
                        Yes
                      </span>
                      <span v-else>
                        No
                      </span>
                    </b-col>
                  </b-row>
                </b-col>

                <b-col
                  cols="12"
                  sm="6"
                >
                  <b-row class="text-muted font-w500 font-size-sm mb-1">
                    <b-col>
                      All work orders require a service?
                    </b-col>
                  </b-row>

                  <b-row class="mb-3">
                    <b-col>
                      <span v-if="client.work_order_charges_require_service">
                        Yes
                      </span>
                      <span v-else>
                        No
                      </span>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>

              <div v-if="allowsNoShow">
                <h4 class="mb-3">
                  No Shows
                </h4>

                <b-row>
                  <b-col
                    cols="12"
                    sm="6"
                  >
                    <b-row class="text-muted font-w500 font-size-sm mb-1">
                      <b-col>
                        Original work orders will be completed?
                      </b-col>
                    </b-row>

                    <b-row class="mb-3">
                      <b-col>
                        <span v-if="client.complete_work_orders">
                          Yes
                        </span>
                        <span v-else>
                          No
                        </span>
                      </b-col>
                    </b-row>
                  </b-col>

                  <b-col
                    cols="12"
                    sm="6"
                  >
                    <b-row class="text-muted font-w500 font-size-sm mb-1">
                      <b-col>
                        No Show Service
                      </b-col>
                    </b-row>

                    <b-row class="mb-3">
                      <b-col v-if="client.no_show_service">
                        <b-link :to="{ name: 'service', params: { id: client.no_show_service.id } }">
                          {{ client.no_show_service.name }}
                        </b-link>
                      </b-col>
                      <b-col v-else>
                        -
                      </b-col>
                    </b-row>
                  </b-col>

                  <b-col
                    cols="12"
                    sm="6"
                  >
                    <b-row class="text-muted font-w500 font-size-sm mb-1">
                      <b-col>
                        Amount Paid to Technician (Cost)
                      </b-col>
                    </b-row>

                    <b-row class="mb-3">
                      <b-col>
                        <money-format
                          :value="client.no_show_cost.amount"
                          :currency-code="client.no_show_cost.currency"
                          subunits-value
                        />
                      </b-col>
                    </b-row>
                  </b-col>

                  <b-col
                    cols="12"
                    sm="6"
                  >
                    <b-row class="text-muted font-w500 font-size-sm mb-1">
                      <b-col>
                        Amount Paid to Client (Price)
                      </b-col>
                    </b-row>

                    <b-row class="mb-3">
                      <b-col>
                        <money-format
                          :value="client.no_show_price.amount"
                          :currency-code="client.no_show_price.currency"
                          subunits-value
                        />
                      </b-col>
                    </b-row>
                  </b-col>
                </b-row>
              </div>
            </b-col>
          </b-row>
        </b-col>

        <b-col md="6">
          <hr class="mt-0 mb-3 d-md-none">
          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Billing Contact Name
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="client.contact_name">
                  {{ client.contact_name }}
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Billing Relationship
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="client.relationship">
                  {{ client.relationship }}
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Billing PO #
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="client.purchase_order_number">
                  {{ client.purchase_order_number }}
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Billing Attention To
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="client.attention_to">
                  {{ client.attention_to }}
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  How to deliver {{ $store.getters.translate('client', 'Client') }} invoices?
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ client.invoice_delivery_method | humanizedFormat | capitalizeFormat }}
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  {{ $store.getters.translate('client', 'Client') }} is exempt from paying taxes?
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <span
                    v-if="client.tax_exempt"
                    variant="warning"
                  >
                    Yes
                  </span>
                  <span
                    v-else
                    variant="success"
                  >
                    No
                  </span>
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Disable automatic sending of invoices?
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <span
                    v-if="client.disable_auto_invoice_delivery"
                    variant="warning"
                  >
                    Yes
                  </span>
                  <span
                    v-else
                    variant="secondary"
                  >
                    No
                  </span>
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  {{ $store.getters.translate('client', 'Client') }} requires a physical invoice?
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <span v-if="client.send_physical_invoice">
                    Yes
                  </span>
                  <span v-else>
                    No
                  </span>
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Invoice Format
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="client.invoice_format && client.invoice_format.name">
                  {{ client.invoice_format.name }}
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row class="mb-3">
            <b-col>
              <contact-infos-list
                :list-func="$contactInfosAPI.getClientsContactInfos"
                :create-func="$contactInfosAPI.createClientsContactInfos"
                :reference-id="client.id"
                reference-type="Client"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-tabs
            content-class="pt-4 mb-3"
            v-model="activeTab"
          >
            <template #tabs-end>
              <div
                v-show="activeTab === 0"
                class="ml-auto mt-3"
              >
                <b-button
                  v-if="$can('create', 'Document')"
                  v-b-modal.new-document-modal
                  variant="outline-secondary"
                >
                  <i class="fa fa-plus" />
                </b-button>
              </div>

              <div
                v-show="activeTab === 1"
                class="ml-auto mt-2"
              >
                <b-button
                  v-if="$can('create', 'Location')"
                  v-b-modal.location-add-modal
                  variant="outline-secondary"
                  v-b-tooltip.hover
                  title="Create a new Location for this Client"
                >
                  <i class="fa fa-plus" />
                  Add Location
                </b-button>
              </div>

              <div
                v-show="activeTab === 2"
                class="ml-auto mt-3"
              >
                <b-button
                  v-if="$can('create', 'Service')"
                  v-b-modal.service-add-modal
                  variant="outline-secondary"
                  v-b-tooltip.hover
                  title="Create a new Service for this Client"
                >
                  <i class="fa fa-plus mr-1" /> Add Service
                </b-button>
              </div>

              <div
                v-show="activeTab === 3"
                class="ml-auto mt-3"
              >
                <b-button
                  variant="outline-secondary"
                  @click="$refs.notesTimeline && $refs.notesTimeline.toggleTimelineForm()"
                >
                  <i class="fa fa-plus mr-1" /> New Note
                </b-button>
              </div>
            </template>

            <b-tab
              title="Documents"
              active
            >
              <documents-table
                :columns="$store.getters.columnsForTable(documentsTableName)"
                :append-to-query="false"
                :show-per-page="false"
                :key="documentsKey"
                :list-provider="$documentsAPI.getClientsDocuments"
                :reference-id="client.id"
              />
            </b-tab>

            <b-tab title="Locations">
              <locations-table
                :filters="{ client_id: client.id, active: true }"
                :columns="locationColumns"
                :append-to-query="false"
                :show-per-page="false"
              />
            </b-tab>

            <b-tab title="Services">
              <services-table
                :filters="{ client_id: client.id, active: true }"
                :columns="serviceColumns"
                :append-to-query="false"
              />
            </b-tab>

            <b-tab>
              <template #title>
                <number-badge
                  title="Notes"
                  :total="notesTotal"
                />
              </template>

              <notes-timeline
                ref="notesTimeline"
                :list-func="$notesAPI.getClientsNotes"
                :create-func="createClientNoteAdapter"
                :reference-id="client.id"
                reference-type="Client"
                hide-new-note-button
                @total-rows-changed="(newTotal) => notesTotal = newTotal"
              />
            </b-tab>
          </b-tabs>
        </b-col>
      </b-row>
    </div>

    <!-- Modals -->
    <clients-edit-modal
      v-if="$can('update', 'Client')"
      v-model="client"
    />
    <clients-settings-modal
      v-if="$can('update', 'Client')"
      v-model="client"
    />
    <clients-billing-details-modal
      v-if="$can('update', 'Client')"
      v-model="client"
    />
    <new-document-modal
      v-if="$can('create', 'Document')"
      :reference-id="client.id"
      :create-provider="$documentsAPI.createClientsDocument"
      @on-save="refreshDocuments()"
      internal
      :key="documentsKey"
    />
    <locations-new-modal
      v-if="$can('create', 'Location')"
      :client_ids="[client.id]"
    />
    <services-new-modal
      v-if="$can('create', 'Service')"
      :client="client"
    />
  </div>
</template>

<script>
import ClientsEditModal from '@/components/admin/clients/EditModal.vue';
import ClientsSettingsModal from '@/components/admin/clients/SettingsModal.vue';
import ClientsBillingDetailsModal from '@/components/admin/clients/BillingDetailsModal.vue';
import LocationsTable from '@/components/admin/locations/Table.vue';
import ServicesTable from '@/components/admin/services/Table.vue';
import NotesTimeline from '@/components/admin/notes/Timeline.vue';
import NumberBadge from '@/components/shared/NumberBadge.vue';
import ContactInfosList from '@/components/admin/contactInfos/CardList.vue';
import LocationsNewModal from '@/components/admin/locations/NewModal.vue';
import ServicesNewModal from '@/components/admin/services/NewModal.vue';
import NetSuiteFormPopover from '@/components/admin/integrationReferences/NetSuiteFormPopover.vue';
import NewDocumentModal from '@/components/admin/documents/NewModal.vue';
import DocumentsTable from '@/components/admin/documents/Table.vue';
import { DOCUMENTS_TABLE_NAME } from '@/constants/documents';
import { CLIENT_LOCATION_TABLE_NAME } from '@/constants/locations';
import { CLIENT_SERVICE_TABLE_NAME } from '@/constants/services';
import { DateTime } from 'luxon';

export default {
  name: 'ClientsShow',
  components: {
    ClientsEditModal,
    ClientsSettingsModal,
    ClientsBillingDetailsModal,
    LocationsTable,
    ServicesTable,
    NotesTimeline,
    NumberBadge,
    LocationsNewModal,
    ServicesNewModal,
    ContactInfosList,
    NetSuiteFormPopover,
    NewDocumentModal,
    DocumentsTable,
  },
  data() {
    return {
      client: null,
      notesTotal: 0,
      clientLocationsTableName: CLIENT_LOCATION_TABLE_NAME,
      clientServicesTableName: CLIENT_SERVICE_TABLE_NAME,
      documentsTableName: DOCUMENTS_TABLE_NAME,
      activeTab: 0,
      documentsKey: `documents-${Date.now()}`,
    };
  },
  computed: {
    hasAnyAddressFields() {
      return (
        (this.client.contact_name || '').length > 0 ||
        (this.client.attention_to || '').length > 0 ||
        (this.client.address || '').length > 0 ||
        (this.client.city || '').length > 0 ||
        (this.client.state || '').length > 0 ||
        (this.client.zip_code || '').length > 0
      );
    },
    allowsNoShow() {
      return this.client.no_show_cost.amount != 0 || this.client.no_show_price.amount != 0 || this.client.no_show_service;
    },
    toggleActiveTooltipText() {
      return !this.client.deleted_at ? 'Deactivate' : 'Reactivate';
    },
    toggleActiveConfirmText() {
      return !this.client.deleted_at
        ? 'Are you sure you want to deactivate the Client?'
        : 'Are you sure you want to reactivate the Client?';
    },
    locationColumns() {
      return this.$store.getters.columnsForTable(this.clientLocationsTableName);
    },
    serviceColumns() {
      return this.$store.getters.columnsForTable(this.clientServicesTableName);
    },
  },
  mounted() {
    this.getClient();
  },
  methods: {
    getClient() {
      this.$clientsAPI
        .get(this.$route.params.id)
        .then((client) => {
          this.client = client;
        })
        .catch(() => {
          this.$router.push('/404');
        });
    },
    toggleActive() {
      this.$swal({
        text: this.toggleActiveConfirmText,
        preConfirm: () => this.$clientsAPI.update(this.$route.params.id, { deleted_at: (!this.client.deleted_at ? DateTime.now().toISODate() : null) }),
      }).then((result) => {
        if (result.value) {
          this.client = result.value;
        }
        result.timer;
      });
    },
    createClientNoteAdapter(referenceId, data) {
      return this.$notesAPI.createClientsNote(referenceId, { ...data, reference_type: 'Client', reference_id: referenceId });
    },
    refreshDocuments() {
      this.documentsKey = `documents-${Date.now()}`;
    },
  },
};
</script>
